import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { History as HistoryIcon, ListFilter, Search } from 'lucide-react';
import { AttemptBadge, AttemptScore } from 'components/history/AttemptStatus';
import { LoadMore } from 'components/ui/pagination';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { formatDayTime } from 'lib/dateFormat';
import type { DateRange } from 'lib/dateRange';
import * as AuthModule from 'modules/auth';
import { isAccessContextReady } from 'modules/auth/access';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useFeature, FEATURES } from 'modules/entitlements';
import * as ResultModule from 'modules/result';
import { canOpenResult } from 'modules/result/access';
import * as Teams from 'modules/teams';
import * as TestModule from 'modules/test';
import {
  HeaderIcon,
  ListCard,
  MobileEmpty,
  MobileError,
  MobileScreen,
  MobileSkeleton,
  ScreenBody,
  ScreenHeader,
  SearchInput,
  Segmented,
  M,
} from 'components/mobile';
import { DateRangeChips, FilterSheet, OptionRows, SheetSection, type SheetOption } from '../filterSheet';

type StatusFilter = 'all' | 'completed' | 'in_progress' | 'abandoned';

/** Attempts fetched per "load more". */
const PAGE_SIZE = 10;

/**
 * The phone History: search and status in the header, group/test/date behind
 * the filter icon, and a card of attempts with "load more". Same API params as
 * the desktop page.
 */
export function HistoryMobile() {
  const { t } = useTranslation();
  const { state: authState } = AuthModule.Context.useAuthContext();
  const userId = authState.user?.id ?? '';
  const accessReady = isAccessContextReady(authState);
  // Staff see the organization's attempts and filter by group and date.
  const canViewOrg = usePermission(PERMISSIONS.TESTS_VIEW_RESULTS);
  const hasTeams = useFeature(FEATURES.TEAMS);

  const [status, setStatus] = useState<StatusFilter>('all');
  const { input: searchInput, setInput: setSearchInput, query: search } = useSearchParamState();
  const [teamId, setTeamId] = useState<string | undefined>();
  const [testId, setTestId] = useState<string | undefined>();
  const [dates, setDates] = useState<DateRange>({});
  const [sheetOpen, setSheetOpen] = useState(false);

  const params = useMemo<Omit<ResultModule.Types.IApi.List.Params, 'page'>>(() => {
    const next: Omit<ResultModule.Types.IApi.List.Params, 'page'> = {
      perPage: PAGE_SIZE,
      ...(status !== 'all' ? { status } : {}),
      ...(search ? { q: search } : {}),
    };
    if (testId) next.testId = testId;
    if (canViewOrg) {
      if (teamId) next.teamId = teamId;
      if (dates.from) next.dateFrom = `${dates.from}T00:00:00`;
      if (dates.to) next.dateTo = `${dates.to}T23:59:59`;
    }
    return next;
  }, [status, search, testId, canViewOrg, teamId, dates]);

  const list = ResultModule.Hooks.useInfiniteList({ params, enabled: accessReady && Boolean(userId) });
  const { items, meta, isLoading, isError, refetch } = list;

  // Filter options load the first time the sheet opens (or when a value is already set).
  const { teams, isLoading: teamsLoading } = Teams.Hooks.useList({
    enabled: accessReady && canViewOrg && hasTeams && (sheetOpen || Boolean(teamId)),
  });
  const loadTests = accessReady && (sheetOpen || Boolean(testId));
  const orgTests = TestModule.Hooks.useList({ params: { perPage: 100 }, enabled: loadTests && canViewOrg });
  const availableTests = TestModule.Hooks.useAvailableTests({ params: { perPage: 100 }, enabled: loadTests && !canViewOrg });
  const testSource = canViewOrg ? orgTests : availableTests;

  const teamOptions: SheetOption[] = (teams ?? []).map((team) => ({ id: team.id, name: team.name }));
  const testOptions: SheetOption[] = (testSource.items ?? [])
    .filter((test) => test.id && test.title)
    .map((test) => ({ id: test.id, name: test.title }));

  const sheetFilters = (testId ? 1 : 0) + (canViewOrg && teamId ? 1 : 0) + (canViewOrg && (dates.from || dates.to) ? 1 : 0);
  const isFiltering = status !== 'all' || Boolean(search) || sheetFilters > 0;

  const clearSheet = () => {
    setTeamId(undefined);
    setTestId(undefined);
    setDates({});
  };
  const clearAll = () => {
    clearSheet();
    setStatus('all');
    setSearchInput('');
  };

  const statusTabs = [
    { value: 'all', label: t('all') },
    { value: 'completed', label: t('completed') },
    { value: 'in_progress', label: t('in_progress') },
    { value: 'abandoned', label: t('abandoned') },
  ];

  return (
    <MobileScreen>
      <ScreenHeader
        title={canViewOrg ? t('nav.history') : t('nav.my_attempts')}
        count={meta.total > 0 ? meta.total : undefined}
        right={
          <HeaderIcon
            icon={<ListFilter size={19} />}
            label={t('filters')}
            dot={sheetFilters > 0}
            onClick={() => setSheetOpen(true)}
          />
        }
      >
        <div className="flex flex-col gap-2.5 pb-3 pt-3">
          <SearchInput value={searchInput} onChange={setSearchInput} placeholder={t('search_by_test_name')} />
          <Segmented
            tabs={statusTabs}
            value={status}
            onChange={(value) => setStatus(value as StatusFilter)}
            aria-label={t('status')}
            className="max-w-full"
          />
        </div>
      </ScreenHeader>

      {isLoading ? (
        <MobileSkeleton />
      ) : isError ? (
        <MobileError onRetry={() => refetch()} />
      ) : items.length === 0 ? (
        <MobileEmpty
          icon={isFiltering ? <Search size={22} /> : <HistoryIcon size={22} />}
          title={isFiltering ? t('no_results') : canViewOrg ? t('no_attempts_in_organization_yet') : t('no_attempts_yet')}
          description={
            isFiltering
              ? t('try_adjusting_your_search_or_filters')
              : canViewOrg
                ? t('assessment_attempts_across_your_organization_will_appear_here')
                : t('when_you_complete_assessments_via_invite_or_from_a_test_link_they_will_appear_here')
          }
          action={
            isFiltering ? (
              <button
                type="button"
                onClick={clearAll}
                className="h-10 rounded-md px-4 text-[14px] font-medium"
                style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
              >
                {t('clear_filters')}
              </button>
            ) : undefined
          }
        />
      ) : (
        <ScreenBody>
          <ListCard>
            {items.map((attempt) => {
              const rowClass = 'flex flex-col gap-1.5 px-3.5 py-3';
              const content = (
                <>
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0 text-[14.5px] font-medium leading-snug" style={{ color: M.ink }}>
                      {attempt.testTitle}
                    </span>
                    <AttemptScore attempt={attempt} className="text-[15px] font-bold" />
                  </span>
                  <span className="flex min-w-0 items-center gap-2 text-[12.5px]" style={{ color: M.ink3 }}>
                    <AttemptBadge attempt={attempt} />
                    <span className="min-w-0 truncate tabular-nums">
                      {canViewOrg && attempt.userName ? `${attempt.userName} · ` : ''}
                      {formatDayTime(attempt.completedAt ?? attempt.startedAt)}
                    </span>
                  </span>
                </>
              );
              // The API refuses the review of one's own running attempt.
              return canOpenResult(attempt, userId) ? (
                <Link key={attempt.id} to={`/app/results/${attempt.id}`} className={rowClass}>
                  {content}
                </Link>
              ) : (
                <div key={attempt.id} className={rowClass}>
                  {content}
                </div>
              );
            })}
          </ListCard>
          <LoadMore
            compact
            className="mt-4"
            shown={items.length}
            total={meta.total}
            step={PAGE_SIZE}
            onLoadMore={() => list.fetchNextPage()}
            loading={list.isFetchingNextPage}
          />
        </ScreenBody>
      )}

      <FilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onClear={sheetFilters > 0 ? clearSheet : undefined}
        total={meta.total}
      >
        {canViewOrg && hasTeams && (
          <SheetSection label={t('team')}>
            <OptionRows
              name={t('team')}
              options={teamOptions}
              selected={teamId}
              onSelect={setTeamId}
              loading={teamsLoading}
              emptyLabel={t('teams.no_teams_title')}
            />
          </SheetSection>
        )}
        <SheetSection label={t('test')}>
          <OptionRows
            name={t('test')}
            options={testOptions}
            selected={testId}
            onSelect={setTestId}
            loading={testSource.isLoading}
            emptyLabel={testSource.isError ? t('failed_to_load') : t('no_tests_yet')}
          />
        </SheetSection>
        {canViewOrg && (
          <SheetSection label={t('date')}>
            <DateRangeChips value={dates} onChange={setDates} />
          </SheetSection>
        )}
      </FilterSheet>
    </MobileScreen>
  );
}

export default HistoryMobile;
